import React from "react";
import { Card, Row, Col, Statistic, Table, Spin, Empty, Tag, Space } from "antd";
import {
  CommentOutlined,
  LikeOutlined,
  SmileOutlined,
  ReadOutlined,
  HeartOutlined,
  UserOutlined,
} from "@ant-design/icons";

const EngagementSection = ({
  commentStats,
  topCommenters,
  reactionStats,
  topReactedVideos,
  postStats,
  loading,
}) => {
  return (
    <div>
      {/* Stats cards row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <CommentCard stats={commentStats} loading={loading.comments} />
        </Col>
        <Col xs={24} lg={8}>
          <ReactionCard stats={reactionStats} loading={loading.reactions} />
        </Col>
        <Col xs={24} lg={8}>
          <PostCard stats={postStats} loading={loading.posts} />
        </Col>
      </Row>

      {/* Reaction Distribution */}
      <div style={{ marginTop: 16 }}>
        <ReactionDistribution stats={reactionStats} loading={loading.reactions} />
      </div>

      {/* Tables row */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <TopCommentersTable users={topCommenters} loading={loading.topCommenters} />
        </Col>
        <Col xs={24} lg={12}>
          <TopReactedTable videos={topReactedVideos} loading={loading.topReacted} />
        </Col>
      </Row>
    </div>
  );
};

const CommentCard = ({ stats, loading }) => {
  if (loading) {
    return <Card size="small"><div style={{ textAlign: "center", padding: 20 }}><Spin /></div></Card>;
  }
  if (!stats) return <Card size="small"><Empty description="Sem dados" /></Card>;

  return (
    <Card
      size="small"
      title={
        <span>
          <CommentOutlined style={{ color: "#13c2c2", marginRight: 8 }} />
          Comentarios
        </span>
      }
    >
      <Row gutter={[8, 12]}>
        <Col span={12}>
          <Statistic title="Total" value={stats.totalComments} valueStyle={{ fontSize: 18 }} />
        </Col>
        <Col span={12}>
          <Statistic title="Usuarios" value={stats.uniqueCommenters} valueStyle={{ fontSize: 18 }} />
        </Col>
        <Col span={12}>
          <Statistic title="Total Likes" value={stats.totalLikes} valueStyle={{ fontSize: 18 }} />
        </Col>
        <Col span={12}>
          <Statistic
            title="Taxa Resposta"
            value={stats.replyRate?.toFixed(1)}
            suffix="%"
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

const ReactionCard = ({ stats, loading }) => {
  if (loading) {
    return <Card size="small"><div style={{ textAlign: "center", padding: 20 }}><Spin /></div></Card>;
  }
  if (!stats) return <Card size="small"><Empty description="Sem dados" /></Card>;

  return (
    <Card
      size="small"
      title={
        <span>
          <SmileOutlined style={{ color: "#722ed1", marginRight: 8 }} />
          Reacoes
        </span>
      }
    >
      <Row gutter={[8, 12]}>
        <Col span={12}>
          <Statistic title="Total" value={stats.totalReactions} valueStyle={{ fontSize: 18 }} />
        </Col>
        <Col span={12}>
          <Statistic title="Usuarios" value={stats.uniqueReactors} valueStyle={{ fontSize: 18 }} />
        </Col>
        <Col span={24}>
          <Statistic
            title="Tipos Diferentes"
            value={stats.distribution?.length || 0}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

const PostCard = ({ stats, loading }) => {
  if (loading) {
    return <Card size="small"><div style={{ textAlign: "center", padding: 20 }}><Spin /></div></Card>;
  }
  if (!stats) return <Card size="small"><Empty description="Sem dados" /></Card>;

  return (
    <Card
      size="small"
      title={
        <span>
          <ReadOutlined style={{ color: "#fa8c16", marginRight: 8 }} />
          Posts
        </span>
      }
    >
      <Row gutter={[8, 12]}>
        <Col span={12}>
          <Statistic title="Leituras" value={stats.totalReads} valueStyle={{ fontSize: 18 }} />
        </Col>
        <Col span={12}>
          <Statistic title="Leitores" value={stats.uniqueReaders} valueStyle={{ fontSize: 18 }} />
        </Col>
        <Col span={12}>
          <Statistic
            title="Claps"
            value={stats.totalClaps}
            prefix={<HeartOutlined />}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title="Media/Post"
            value={stats.avgClapsPerPost?.toFixed(1)}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

const ReactionDistribution = ({ stats, loading }) => {
  if (loading) {
    return (
      <Card title="Distribuicao de Reacoes" size="small">
        <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
      </Card>
    );
  }
  if (!stats?.distribution || stats.distribution.length === 0) return null;

  const top15 = stats.distribution.slice(0, 15);

  return (
    <Card title="Distribuicao de Reacoes" size="small">
      <Space wrap size={[8, 8]}>
        {top15.map((item, idx) => (
          <Tag
            key={idx}
            style={{
              fontSize: 14,
              padding: "4px 10px",
              borderRadius: 16,
            }}
          >
            {item.reaction} {item.count}
          </Tag>
        ))}
        {stats.distribution.length > 15 && (
          <Tag style={{ fontSize: 12, padding: "4px 10px" }}>
            +{stats.distribution.length - 15} mais
          </Tag>
        )}
      </Space>
    </Card>
  );
};

const TopCommentersTable = ({ users, loading }) => {
  const columns = [
    {
      title: "Usuario",
      dataIndex: "user",
      key: "user",
      ellipsis: true,
      render: (val) => (
        <span>
          <UserOutlined style={{ marginRight: 6, color: "#888" }} />
          {val?.substring(0, 12)}...
        </span>
      ),
    },
    {
      title: "Comentarios",
      dataIndex: "totalComments",
      key: "totalComments",
      width: 110,
      sorter: (a, b) => a.totalComments - b.totalComments,
    },
    {
      title: "Likes",
      dataIndex: "totalLikes",
      key: "totalLikes",
      width: 80,
      render: (val) => (
        <span>
          <LikeOutlined style={{ marginRight: 4, color: "#1890ff" }} />
          {val}
        </span>
      ),
    },
  ];

  return (
    <Card title="Top Comentaristas" size="small">
      <Table
        columns={columns}
        dataSource={users}
        rowKey="user"
        loading={loading}
        pagination={false}
        size="small"
      />
    </Card>
  );
};

const TopReactedTable = ({ videos, loading }) => {
  const columns = [
    {
      title: "Video",
      dataIndex: "videoName",
      key: "videoName",
      ellipsis: true,
    },
    {
      title: "Reacoes",
      dataIndex: "totalReactions",
      key: "totalReactions",
      width: 100,
      sorter: (a, b) => a.totalReactions - b.totalReactions,
      render: (val) => (
        <Tag color="purple">{val}</Tag>
      ),
    },
  ];

  return (
    <Card title="Videos Mais Reagidos" size="small">
      <Table
        columns={columns}
        dataSource={videos}
        rowKey="videoId"
        loading={loading}
        pagination={false}
        size="small"
      />
    </Card>
  );
};

export default EngagementSection;
